<template>
  <div class="force-vector-viz">
    <div class="viz-header" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex>${{ formulaText }}$</div>
    </div>

    <div class="main-layout vertical">
      <!-- 混合渲染交互区 -->
      <div class="canvas-mixed interactive">
        <svg width="100%" height="280" viewBox="0 0 400 280">
           <!-- 背景网格 -->
           <defs>
              <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                 <path d="M 20 0 L 0 0 0 20" fill="none" stroke="rgba(255,255,255,0.05)" stroke-width="0.5"/>
              </pattern>
           </defs>
           <rect width="400" height="280" fill="url(#grid)" />

           <!-- 坐标原点 (200, 140) -->
           <line x1="200" y1="0" x2="200" y2="280" stroke="rgba(255,255,255,0.1)" />
           <line x1="0" y1="140" x2="400" y2="140" stroke="rgba(255,255,255,0.1)" />

           <!-- 作用点 (Pivot) -->
           <circle v-if="mode === 'elements'" cx="200" cy="140" r="6" fill="#fff" stroke="#f43f5e" stroke-width="2" />
           <text v-if="mode === 'elements'" x="208" y="135" fill="#fff" font-size="12" font-weight="900">作用点 (Origin)</text>

           <!-- 辅助虚线 (平行四边形) -->
           <line v-if="showHelper && mode !== 'elements'" :x1="f1End.x" :y1="f1End.y" :x2="fResultEnd.x" :y2="fResultEnd.y" stroke="rgba(0, 210, 255, 0.3)" stroke-dasharray="4,2" />
           <line v-if="showHelper && mode !== 'elements'" :x1="f2End.x" :y1="f2End.y" :x2="fResultEnd.x" :y2="fResultEnd.y" stroke="rgba(0, 210, 255, 0.3)" stroke-dasharray="4,2" />

           <!-- 分力 F1 -->
           <line x1="200" y1="140" :x2="f1End.x" :y2="f1End.y" stroke="#ff4757" stroke-width="3" marker-end="url(#arr-f1)" />
           <text :x="f1End.x + 5" :y="f1End.y" fill="#ff4757" font-size="14" font-weight="900">F1 = {{ f1 }}N</text>

           <!-- 分力 F2 -->
           <line v-if="mode !== 'elements'" x1="200" y1="140" :x2="f2End.x" :y2="f2End.y" stroke="#2ed573" stroke-width="3" marker-end="url(#arr-f2)" />
           <text v-if="mode !== 'elements'" :x="f2End.x + 5" :y="f2End.y" fill="#2ed573" font-size="14" font-weight="900">F2 = {{ f2 }}N</text>

           <!-- 合力 F -->
           <line v-if="mode !== 'elements'" x1="200" y1="140" :x2="fResultEnd.x" :y2="fResultEnd.y" stroke="#ffd700" stroke-width="4" marker-end="url(#arr-fr)" />
           <text v-if="mode !== 'elements'" :x="fResultEnd.x + 10" :y="fResultEnd.y" fill="#ffd700" font-size="16" font-weight="bold">F = {{ fResult.toFixed(1) }}N</text>
           
           <defs>
              <marker id="arr-f1" markerWidth="6" markerHeight="6" refX="6" refY="3" orient="auto"><polygon points="0 0, 6 3, 0 6" fill="#ff4757" /></marker>
              <marker id="arr-f2" markerWidth="6" markerHeight="6" refX="6" refY="3" orient="auto"><polygon points="0 0, 6 3, 0 6" fill="#2ed573" /></marker>
              <marker id="arr-fr" markerWidth="8" markerHeight="8" refX="8" refY="4" orient="auto"><polygon points="0 0, 8 4, 0 8" fill="#ffd700" /></marker>
           </defs>
        </svg>

        <div class="hud-angle" v-if="mode !== 'elements'">夹角 θ = {{ theta }}°</div>
      </div>

      <!-- 控制面板 -->
      <div class="bottom-controls">
         <div class="control-card glass">
            <div class="row">
               <div class="col">
                  <label>{{ mode === 'elements' ? '力的大小 (Magnitude)' : 'F1 大小 (N)' }}</label>
                  <el-slider v-model="f1" :min="10" :max="120" size="mini"></el-slider>
               </div>
               <div class="col" v-if="mode !== 'elements'">
                  <label>F2 大小 (N)</label>
                  <el-slider v-model="f2" :min="10" :max="100" size="mini"></el-slider>
               </div>
            </div>
            <div class="row" v-if="mode !== 'elements'">
               <div class="col-full">
                  <label>夹角 θ (°)</label>
                  <el-slider v-model="theta" :min="0" :max="180" size="mini"></el-slider>
               </div>
            </div>
            <div class="row" v-if="mode === 'elements'">
               <div class="col-full">
                  <label>力的方向 (Angle)</label>
                  <el-slider v-model="theta" :min="0" :max="360" size="mini"></el-slider>
               </div>
            </div>
         </div>

         <div class="theory-box">
            <p><strong>规律：</strong>{{ theoryText }}</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ForceVectorViz',
  props: { knowledgePoint: { type: String, default: '' } },
  data() {
    return {
      mode: 'resultant', // resultant, elements, components
      f1: 80,
      f2: 50,
      theta: 60,
      showHelper: true
    }
  },
  watch: {
    knowledgePoint: {
       handler(val) {
          const kp = (val || '').toString();
          if (kp.includes('三要素')) this.mode = 'elements';
          else if (kp.includes('分解')) this.mode = 'components';
          else this.mode = 'resultant';
       },
       immediate: true
    }
  },
  computed: {
    briefText() {
       if (this.mode === 'elements') return '力的三要素：大小、方向、作用点';
       if (this.mode === 'components') return '力的分解：将合力按实际效果拆解为分力';
       return '力的合成与表示：平行四边形定则';
    },
    formulaText() {
       if (this.mode === 'elements') return 'Vector = (M, θ, O)';
       return 'F = √(F₁² + F₂² + 2F₁F₂cosθ)';
    },
    theoryText() {
       if (this.mode === 'elements') return '力的作用效果由三个因素共同决定：大小（箭头长短）、方向（箭头所指）、作用点（箭头起始位置）。改变其中任何一个，力的效果都会改变。';
       if (this.mode === 'components') return '力的分解是合成的逆过程。同一个力可以分解为无数对分力，但在实际应用中，我们通常按力的实际作用效果（如沿斜面和垂直斜面）进行分解。';
       return '合力的大小取决于分力的大小与夹角。夹角越小，合力越大；夹角越大，合力越小。合力的范围在 |F1-F2| ≤ F ≤ |F1+F2| 之间。';
    },
    rad() { return (this.theta * Math.PI) / 180; },
    f1End() {
       // In elements mode, theta is just the direction of F1
       const ang = this.mode === 'elements' ? -this.rad : 0; // standard horizontal for f1 in resultant mode
       return { x: 200 + this.f1 * Math.cos(ang), y: 140 + this.f1 * Math.sin(ang) };
    },
    f2End() {
       return { 
          x: 200 + this.f2 * Math.cos(-this.rad), 
          y: 140 + this.f2 * Math.sin(-this.rad) 
       };
    },
    fResult() {
       return Math.sqrt(this.f1**2 + this.f2**2 + 2 * this.f1 * this.f2 * Math.cos(this.rad));
    },
    fResultEnd() {
       // Parallelogram diagonal
       // F1 is at (f1, 0), F2 is at (f2*cos, f2*sin)
       return {
          x: 200 + this.f1 + this.f2 * Math.cos(-this.rad),
          y: 140 + this.f2 * Math.sin(-this.rad)
       };
    }
  }
}
</script>

<style scoped lang="scss">
.force-vector-viz {
  flex: 1; display: flex; flex-direction: column; gap: 12px; height: 100%; overflow: hidden;
}
.viz-header { display: flex; justify-content: space-between; align-items: center; .brief { font-size: 1rem; color: #1e293b; font-weight: 700; } .formula-chip { color: #854d0e; background: #fefce8; border: 1px solid #fef08a; padding: 6px 16px; border-radius: 8px; font-weight: bold; font-family: serif; } }
.canvas-mixed {
  flex: 1.3; background: #000; border-radius: 12px; position: relative; border: 1px solid rgba(255,255,255,0.05); overflow: hidden;
  .hud-angle { position: absolute; top: 15px; right: 15px; color: #ffd700; font-size: 0.8rem; background: rgba(0,0,0,0.5); padding: 5px 10px; border-radius: 4px; }
}
.bottom-controls { flex: 1; display: flex; flex-direction: column; gap: 10px; padding-bottom: 10px; overflow-y: auto; }
.control-card { background: rgba(255,255,255,0.03); border: 1px solid #e2e8f0; border-radius: 12px; padding: 15px; 
  .row { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 10px; .col-full { grid-column: 1 / span 2; } label { font-size: 0.75rem; color: #64748b; font-weight: 700; display: block; margin-bottom: 5px; } }
}
.theory-box { background: rgba(255, 215, 0, 0.05); padding: 12px 18px; border-radius: 12px; border-left: 4px solid #facc15; p { margin: 0; font-size: 0.8rem; color: #475569; line-height: 1.6; strong { color: #1e293b; } } }
</style>
